import { Stack } from "@mantine/core"
import {
  TextInput,
  NumberInput,
  ColorInput,
  Switch,
  FileInput,
  Label,
} from "@tfg/ui"
import _, { noop } from "lodash"
import { Dispatch, SetStateAction } from "react"
import { ZodNullable, ZodObject, ZodTypeAny, z } from "zod"

import { ZodFormObjectTypeDescription } from "@tfg/types/constants/zodFromInputTypes"
import { type FormFileType } from "@tfg/types/schemas/file"

import { useAuth } from "~/store/auth-context"
import { createObjectURLFromBuffer } from "~/utils/file"
import { InputWrapper, ZodFormBuilderEnum } from "~/utils/zod/zodFormComponents"
import {
  cleanEmptyObjects,
  getClassPropsShapeAndKey,
  getCssVariableShape,
  getKeyFromPath,
  getZodFieldType,
  isCSSColorSchema,
} from "~/utils/zod/zodHelpers"

export type ZodFormBuilderProps<
  T extends ZodObject<Record<string, ZodTypeAny>>,
> = {
  schema: T
  values: z.infer<T>
  onChange: (values: z.infer<T>) => void
  inlineLabel?: boolean
  fetchKeyTranslation: (path: string[]) => string
  resolvePermission: (key: string) => boolean
  errors: Partial<Record<keyof T, string>>
  setErrors: Dispatch<SetStateAction<Partial<Record<keyof T, string>>>>
  isDirty?: boolean
  setIsDirty?: Dispatch<SetStateAction<boolean>>
  tooltipsConfig?: Record<string, string>
  onFileUpload?: (value: File) => Promise<string | null>
  onFileDelete?: (path: string) => Promise<void>
}

const isZodEnum = (fieldShape: z.ZodTypeAny): fieldShape is z.ZodEnum<any> => {
  return fieldShape instanceof z.ZodEnum
}

export const ZodFormBuilder = <
  T extends ZodObject<Record<string, ZodTypeAny>>,
>({
  schema,
  values,
  onChange,
  inlineLabel = false,
  fetchKeyTranslation = (path: string[]) => path[path.length - 1],
  resolvePermission,
  errors,
  setErrors,
  isDirty,
  setIsDirty = () => noop,
  tooltipsConfig,
  onFileUpload,
  onFileDelete,
}: ZodFormBuilderProps<T>) => {
  const { isAdmin } = useAuth()

  const schemaObjectShape = schema.shape
  const schemaObjectKeys = Object.keys(schemaObjectShape)
  const handleFieldChange = (
    path: string[],
    newValue: string | number | boolean | FormFileType | null,
    fieldShape: z.ZodTypeAny
  ) => {
    if (!isDirty) {
      setIsDirty(true)
    }
    const clone = _.cloneDeep(values)
    if (newValue === null) {
      _.unset(clone, path)
      onChange(clone)
    } else {
      _.set(clone, path, newValue)
      const result = fieldShape.safeParse(newValue)
      if (!result.success) {
        const nested = {}
        _.set(
          nested,
          path,
          result.error.issues.map((issue) => issue.message).join(", ")
        )
        setErrors((prev) => _.merge({}, prev, nested))
      } else {
        setErrors((prev) => {
          const clone = _.cloneDeep(prev)
          _.unset(clone, path)
          const cleanedClone = cleanEmptyObjects(clone)
          return cleanedClone ?? {}
        })
      }
      onChange(clone)
    }
  }

  const renderZodField = ({
    shape,
    path,
  }: {
    shape: z.ZodRawShape
    path: string[]
  }) => {
    const key = getKeyFromPath(path)

    const isInSubscriptionPlan = !isAdmin ? resolvePermission(path[0]) : true

    const [fieldType, fieldShape] = getZodFieldType(shape, key)
    const toolTip = tooltipsConfig?.[path.join(".")]
    switch (fieldType) {
      case ZodFormObjectTypeDescription.ZOD_STYLING_CLASS: {
        const [classPropsShape, classPropsKeys] =
          getClassPropsShapeAndKey(fieldShape)
        return (
          <Stack key={`${path.join("-")}-class-props`} gap="sm">
            {classPropsKeys.map((propsKey) => {
              return (
                <div key={`${key}-${path.join("-")}-${propsKey}`}>
                  {renderZodField({
                    shape: classPropsShape,
                    path: [key, "props", propsKey],
                  })}
                </div>
              )
            })}
          </Stack>
        )
      }
      case ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE: {
        const path = [key, "value"]
        let cssFieldShape = getCssVariableShape(fieldShape)

        // if is a nullable css variable we must get its inner type
        if (cssFieldShape instanceof ZodNullable) {
          cssFieldShape = cssFieldShape._zod.def.innerType as z.ZodTypeAny
        }

        if (isCSSColorSchema(cssFieldShape)) {
          return (
            <InputWrapper key={`${key}-${path.join("-")}-color-input`}>
              <ColorInput
                key={`${key}-${path.join("-")}`}
                label={
                  <Label
                    label={fetchKeyTranslation?.(path)}
                    locked={!isInSubscriptionPlan}
                    tooltip={
                      !isInSubscriptionPlan
                        ? "This field is not available in your subscription plan"
                        : undefined
                    }
                    w="100%"
                  />
                }
                inlineLabel={inlineLabel}
                value={_.get(values, path)}
                error={_.get(errors, path)}
                disabled={!isInSubscriptionPlan}
                onChange={(value) =>
                  handleFieldChange(path, value, cssFieldShape)
                }
              />
            </InputWrapper>
          )
        }
        if (isZodEnum(cssFieldShape)) {
          return (
            <ZodFormBuilderEnum
              key={`${key}-${path.join("-")}-enum`}
              fieldShape={cssFieldShape}
              path={path}
              values={_.get(values, path)}
              errors={_.get(errors, path)}
              isInSubscriptionPlan={isInSubscriptionPlan}
              fetchKeyTranslation={fetchKeyTranslation}
              inlineLabel={inlineLabel}
              schemaKey={key}
              handleFieldChange={handleFieldChange}
            />
          )
        }
        return (
          <InputWrapper key={`${key}-${path.join("-")}-text-input`}>
            <TextInput
              key={`${key}-${path.join("-")}`}
              label={
                <Label
                  label={fetchKeyTranslation?.(path)}
                  locked={!isInSubscriptionPlan}
                  tooltip={
                    !isInSubscriptionPlan
                      ? "This field is not available in your subscription plan"
                      : undefined
                  }
                  w="100%"
                />
              }
              inlineLabel={inlineLabel}
              value={_.get(values, path)}
              error={_.get(errors, path)}
              disabled={!isInSubscriptionPlan}
              onChange={(value) =>
                handleFieldChange(path, value.target.value, cssFieldShape)
              }
            />
          </InputWrapper>
        )
      }
      case ZodFormObjectTypeDescription.ZOD_FILE: {
        const currentFile = _.get(values, path)
        return (
          <InputWrapper key={`${key}-${path.join("-")}-file-input`}>
            <FileInput
              key={key}
              inlineLabel={inlineLabel}
              accept="image/png, image/jpeg, image/svg, image/jpg, image/webp, image/svg+xml"
              label={
                <Label
                  label={fetchKeyTranslation?.(path)}
                  locked={!isInSubscriptionPlan}
                  tooltip={
                    !isInSubscriptionPlan
                      ? "This field is not available in your subscription plan"
                      : undefined
                  }
                  w="100%"
                />
              }
              value={currentFile ?? null}
              error={_.get(errors, path)}
              clearable={true}
              disabled={!isInSubscriptionPlan}
              onChange={async (file: File | null) => {
                if (!file) {
                  handleFieldChange(path ?? [key], null, fieldShape)
                  if (onFileDelete) {
                    await onFileDelete?.(_.get(values, path).path)
                  }
                  return
                }
                if (onFileUpload) {
                  const uploadedPath = await onFileUpload?.(file)
                  if (!uploadedPath) {
                    return
                  }
                  if (currentFile && onFileDelete) {
                    onFileDelete(currentFile.path)
                  }
                  const url = createObjectURLFromBuffer(
                    await file.arrayBuffer(),
                    file.type
                  )
                  handleFieldChange(
                    path ?? [key],
                    uploadedPath
                      ? {
                          path: uploadedPath,
                          name: file.name,
                          url,
                        }
                      : null,
                    fieldShape
                  )
                }
              }}
            />
          </InputWrapper>
        )
      }
      case "string": {
        if (isCSSColorSchema(fieldShape)) {
          return (
            <InputWrapper key={`${key}-${path.join("-")}-color-input`}>
              <ColorInput
                key={key}
                label={
                  <Label
                    label={fetchKeyTranslation?.(path)}
                    locked={!isInSubscriptionPlan}
                    tooltip={
                      !isInSubscriptionPlan
                        ? "This field is not available in your subscription plan"
                        : undefined
                    }
                    w="100%"
                  />
                }
                inlineLabel={inlineLabel}
                value={_.get(values, path)}
                error={_.get(errors, path)}
                disabled={!isInSubscriptionPlan}
                onChange={(value) =>
                  handleFieldChange(path ?? [key], value, fieldShape)
                }
              />
            </InputWrapper>
          )
        }
        return (
          <InputWrapper key={`${key}-${path.join("-")}-text-input`}>
            <TextInput
              key={key}
              label={
                <Label
                  label={fetchKeyTranslation?.(path)}
                  locked={!isInSubscriptionPlan}
                  tooltip={
                    !isInSubscriptionPlan
                      ? "This field is not available in your subscription plan"
                      : undefined
                  }
                  w="100%"
                />
              }
              inlineLabel={inlineLabel}
              value={_.get(values, path)}
              error={_.get(errors, path)}
              disabled={!isInSubscriptionPlan}
              onChange={(value) =>
                handleFieldChange(path ?? [key], value.target.value, fieldShape)
              }
            />
          </InputWrapper>
        )
      }
      case "number": {
        return (
          <InputWrapper key={`${key}-${path.join("-")}-number-input`}>
            <NumberInput
              key={key}
              label={
                <Label
                  label={fetchKeyTranslation?.(path)}
                  locked={!isInSubscriptionPlan}
                  tooltip={
                    !isInSubscriptionPlan
                      ? "This field is not available in your subscription plan"
                      : undefined
                  }
                  w="100%"
                />
              }
              inlineLabel={inlineLabel}
              value={_.get(values, path)}
              error={_.get(errors, path)}
              tooltip={toolTip}
              disabled={!isInSubscriptionPlan}
              onChange={(value) =>
                handleFieldChange(path ?? [key], value, fieldShape)
              }
            />
          </InputWrapper>
        )
      }
      case "boolean": {
        return (
          <InputWrapper key={`${key}-${path.join("-")}-switch`}>
            <Switch
              key={key}
              label={
                <Label
                  label={fetchKeyTranslation?.(path)}
                  locked={!isInSubscriptionPlan}
                  tooltip={
                    !isInSubscriptionPlan
                      ? "This field is not available in your subscription plan"
                      : undefined
                  }
                  w="100%"
                />
              }
              checked={_.get(values, path)}
              inlineLabel={inlineLabel}
              error={_.get(errors, path)}
              toolTip={toolTip}
              disabled={!isInSubscriptionPlan}
              onChange={(value) =>
                handleFieldChange(path ?? [key], value, fieldShape)
              }
            />
          </InputWrapper>
        )
      }
      case "enum": {
        if (!isZodEnum(fieldShape)) {
          return null
        }
        return (
          <ZodFormBuilderEnum
            key={`${key}-${path.join("-")}-enum`}
            fieldShape={fieldShape}
            path={path}
            values={_.get(values, path)}
            errors={_.get(errors, path)}
            isInSubscriptionPlan={isInSubscriptionPlan}
            fetchKeyTranslation={fetchKeyTranslation}
            inlineLabel={inlineLabel}
            schemaKey={key}
            handleFieldChange={handleFieldChange}
          />
        )
      }
      default:
        return null
    }
  }

  return (
    <>
      {schemaObjectKeys.map((key) =>
        renderZodField({ shape: schemaObjectShape, path: [key] })
      )}
    </>
  )
}
